// AccountTypesSection · institutional broker tier display.
// Iter-29 rebuild: replaces the chunky tier-colored cards with a single
// uniform datasheet card per tier. Identity is carried by typography
// and tier codes (CL · PR · EL · SO), not by gradient blobs or coloured
// CTAs. Each card streams the LIVE EURUSD spread for its tier via
// /api/account/{tier}/snapshot so the visitor sees real numbers, not
// marketing claims.
//
// Compare panel is unchanged — it already reads as a dense matrix.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, GitCompare, Check, Minus, Filter, ChevronDown, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { REGISTER_URL } from "@/data/site";
import { useCtaTarget } from "@/lib/useCtaTarget";
import { useTheme } from "@/contexts/ThemeContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

const API = process.env.REACT_APP_BACKEND_URL;

/* Per-tier identity tokens · institutional palette restored.
   Exported so other comparison surfaces (e.g. /account-comparison)
   can re-use the same colour tokens to colour their tier columns. */
export const TIER_META = {
  classic: {
    code: "CL",
    tagline: "Standard MT5 account · entry level",
    accent: "#67e8f9",
    accentDeep: "#0891b2",
    ctaText: "#06222a",
    ctaTextLight: "#ffffff",
    gradient: "linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%)",
    gradientLight: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    typicalSpread: "1.0 pip",
    commissionLine: "$0 commission",
    commissionLineKey: "ui.accountTypes.commission.zero",
    leverage: "1:1000",
    minLot: "0.01",
    accentLabel: "0 commission",
    accentLabelKey: "ui.accountTypes.accent.zeroCommission",
    href: REGISTER_URL,
    demo: "https://demo.ofinancial.markets/signup",
  },
  premier: {
    code: "PR",
    tagline: "Raw-spread account · active intraday",
    // Polished platinum/silver in dark mode · deep slate solid in light
    // mode so the silver shimmer doesn't disappear on white.
    accent: "#cbd5e1",
    accentDeep: "#475569",
    ctaText: "#0f172a",
    ctaTextLight: "#ffffff",
    gradient: "linear-gradient(135deg, #f8fafc 0%, #cbd5e1 22%, #94a3b8 48%, #cbd5e1 62%, #64748b 80%, #1e293b 100%)",
    gradientLight: "linear-gradient(135deg, #475569 0%, #334155 100%)",
    typicalSpread: "0.3 pip",
    commissionLine: "$3.00 · per lot per side",
    commissionLineKey: "ui.accountTypes.commission.perLotPerSide",
    commissionAmount: "$3.00",
    leverage: "1:1000",
    minLot: "0.01",
    accentLabel: "Raw spread",
    accentLabelKey: "ui.accountTypes.accent.rawSpread",
    href: REGISTER_URL,
    demo: "https://demo.ofinancial.markets/signup",
  },
  elite: {
    code: "EL",
    tagline: "Institutional · negotiable commissions",
    accent: "#fbe7a3",
    accentDeep: "#d4af37",
    // Gold reads well on both themes · keep the dark ctaText in both
    // modes because cream-yellow on white still works with dark text.
    ctaText: "#2a1f04",
    ctaTextLight: "#1f1602",
    gradient: "linear-gradient(135deg, #fbe7a3 0%, #d4af37 100%)",
    gradientLight: "linear-gradient(135deg, #d4af37 0%, #a37f1f 100%)",
    typicalSpread: "0.1 pip",
    commissionLine: "$2.50 · per lot per side",
    commissionLineKey: "ui.accountTypes.commission.perLotPerSide",
    commissionAmount: "$2.50",
    leverage: "1:500",
    minLot: "0.01",
    accentLabel: "Low commission",
    accentLabelKey: "ui.accountTypes.accent.lowCommission",
    href: REGISTER_URL,
    demo: "https://demo.ofinancial.markets/signup",
  },
  social: {
    code: "SO",
    tagline: "Copy-trading · in your Trader's Room",
    accent: "#86efac",
    accentDeep: "#16a34a",
    ctaText: "#042a14",
    ctaTextLight: "#ffffff",
    gradient: "linear-gradient(135deg, #86efac 0%, #16a34a 100%)",
    gradientLight: "linear-gradient(135deg, #16a34a 0%, #0f7a37 100%)",
    typicalSpread: "1.0 pip",
    commissionLine: "$0 commission",
    commissionLineKey: "ui.accountTypes.commission.zero",
    leverage: "1:500",
    minLot: "0.01",
    accentLabel: "Copy top traders",
    accentLabelKey: "ui.accountTypes.accent.copyTopTraders",
    href: "https://live.ofinancial.markets/link/aysaod",
    demo: "https://demo.ofinancial.markets/signup",
  },
  cent: {
    code: "CE",
    tagline: "Micro-lot cent account · trade in USC",
    accent: "#fdba74",
    accentDeep: "#ea580c",
    ctaText: "#3a1a02",
    ctaTextLight: "#ffffff",
    gradient: "linear-gradient(135deg, #fed7aa 0%, #fb923c 100%)",
    gradientLight: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
    typicalSpread: "1.0 pip",
    commissionLine: "$0 commission",
    commissionLineKey: "ui.accountTypes.commission.zero",
    leverage: "1:500",
    minLot: "0.01",
    accentLabel: "Micro-lot · USC",
    accentLabelKey: "ui.accountTypes.accent.microLot",
    href: REGISTER_URL,
    demo: "https://demo.ofinancial.markets/signup",
  },
};

const KEYS = ["classic", "premier", "elite", "social", "cent"];

// lg 6-col track: 3 flagship tiers span cols 1-2/3-4/5-6 on row 1;
// Social starts at col 2 and Cent flows to col 4-5 → the pair sits centered.
const TIER_SPAN = {
  classic: "lg:col-span-2",
  premier: "lg:col-span-2",
  elite: "lg:col-span-2",
  social: "lg:col-span-2 lg:col-start-2",
  cent: "lg:col-span-2",
};

const getCompareRows = (t) => [
  { label: t("ui.accountTypes.compareRows.minDeposit"),    lens: "cost",      get: (a) => a.minDeposit },
  { label: t("ui.accountTypes.compareRows.spread"),        lens: "cost",      get: (a) => `${a.spreadFrom} pips` },
  { label: t("ui.accountTypes.compareRows.commission"),    lens: "cost",      get: (a) => a.commission },
  { label: t("ui.accountTypes.compareRows.leverage"),      lens: "limits",    get: (a) => a.leverage },
  { label: t("ui.accountTypes.compareRows.execution"),     lens: "execution", get: (a) => a.execution },
  { label: t("ui.accountTypes.compareRows.baseCurrencies"),lens: "limits",    get: (a) => a.baseCurrencies.join(" · ") },
  { label: t("ui.accountTypes.compareRows.lots"),          lens: "limits",    get: (a) => `${a.minLot} / ${a.maxLot}` },
  { label: t("ui.accountTypes.compareRows.eas"),           lens: "platform",  get: () => true },
  { label: t("ui.accountTypes.compareRows.hedging"),       lens: "execution", get: () => true },
  { label: t("ui.accountTypes.compareRows.islamic"),       lens: "platform",  get: (a) => a.label === "Classic" || a.label === "Cent" },
  { label: t("ui.accountTypes.compareRows.vps"),           lens: "platform",  get: (a) => a.label === "Premier" || a.label === "Elite" },
  { label: t("ui.accountTypes.compareRows.manager"),       lens: "platform",  get: () => true },
  { label: t("ui.accountTypes.compareRows.copy"),          lens: "platform",  get: (a) => a.label === "Social" },
];

const getLenses = (t) => [
  { key: "all",       label: t("ui.accountTypes.lenses.all")       },
  { key: "cost",      label: t("ui.accountTypes.lenses.cost")      },
  { key: "execution", label: t("ui.accountTypes.lenses.execution") },
  { key: "platform",  label: t("ui.accountTypes.lenses.platform")  },
  { key: "limits",    label: t("ui.accountTypes.lenses.limits")    },
];

function CompareCell({ value }) {
  if (value === true)  return <Check size={15} className="mx-auto text-[color:var(--brand)]" strokeWidth={2.4} />;
  if (value === false) return <Minus size={13} className="mx-auto text-text-3" strokeWidth={2.2} />;
  return <span className="font-mono text-[12.5px] tabular-nums text-text-1">{value}</span>;
}

/* Compact variant for the per-row 4-up mobile matrix · smaller text,
 * tighter tracking, allows wrapping so longer values like
 * "EUR · USD · GBP · ZAR" still fit inside a ~80 px cell. */
function CompactCompareCell({ value, accent }) {
  if (value === true)  return <Check size={14} className="mx-auto" strokeWidth={2.4} style={{ color: accent }} />;
  if (value === false) return <Minus size={12} className="mx-auto text-text-3" strokeWidth={2.2} />;
  return (
    <span
      className="block font-mono text-[10.5px] tabular-nums text-text-1 leading-tight break-words"
      style={{ wordBreak: "break-word" }}
    >
      {value}
    </span>
  );
}

/* ============ Live spread chip · streams /api/account/{tier}/snapshot
 * every 5 seconds. Shows EURUSD bid + the real tier spread in mono.
 * Falls back to the tier's typical spread if the price hub is down.
 * Accent color is tier-specific so the chip carries the tier identity. */
function LiveSpreadChip({ tier, typicalSpread, accent, accentDeep }) {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState(null);
  const { theme } = useTheme();
  const isLight = theme === "light";
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetch(`${API}/api/account/${tier}/snapshot`)
        .then((r) => r.json())
        .then((d) => { if (!cancelled) setSnapshot(d); })
        .catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tier]);

  const eu = snapshot?.quotes?.find((q) => q.symbol === "EURUSD");
  const isLive = snapshot?.feed_status === "connected" && eu;
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border mt-5"
      style={{
        borderColor: `color-mix(in srgb, ${accentDeep} 22%, var(--line))`,
        background: `color-mix(in srgb, ${accentDeep} 5%, var(--surface-2))`,
      }}
      data-testid={`tier-${tier}-live-chip`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          {isLive && (
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
              style={{ background: accentDeep }}
            />
          )}
          <span
            className="relative inline-flex rounded-full h-1.5 w-1.5"
            style={{ background: isLive ? accentDeep : "var(--text-3)" }}
          />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-text-3 truncate">
          EURUSD · {t("ui.accountTypes.live")}
        </span>
      </div>
      <span
        className="font-mono tabular-nums text-[11px] px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap"
        style={{
          color: isLight ? accentDeep : accent,
          background: `color-mix(in srgb, ${accentDeep} 14%, transparent)`,
        }}
      >
        {isLive ? `${eu.spread.toFixed(1)} pip` : typicalSpread}
      </span>
    </div>
  );
}

/* ============ Institutional tier card · square shell, tier-coloured details ============ */
function TierCard({ k, delay, spanClass = "" }) {
  const { t } = useTranslation();
  // Live account data · admin-edited overrides merged on top of the
  // static defaults from site.js (see SiteSettingsContext).
  const { featuredTier, featuredLabel, accounts: ACCOUNTS } = useSiteSettings();
  const a = ACCOUNTS[k];
  const m = TIER_META[k];
  const { theme } = useTheme();
  const isLight = theme === "light";
  const isFeatured = featuredTier === k && !!featuredLabel;
  // Tier CTA palette flips per theme · the pastel gradients used in
  // dark mode disappear on white, so light mode falls back to a deeper
  // solid-ish gradient with white text.
  const ctaGradient = isLight ? m.gradientLight : m.gradient;
  const ctaTextColor = isLight ? m.ctaTextLight : m.ctaText;
  // 4-row spec grid · the dense data block that institutional broker
  // datasheets always lead with. Identity is carried by the top accent
  // stripe, the inline tier code chip, the highlight pill and the CTA —
  // the card SHELL stays uniform so all 4 tiers read as siblings.
  // Resolve the commission line through i18n · static $X amount is kept
  // verbatim, only the "per lot per side"/"commission" copy localizes.
  const commissionLine = m.commissionAmount
    ? t(m.commissionLineKey, { amount: m.commissionAmount })
    : t(m.commissionLineKey, m.commissionLine);
  const specs = [
    { k: t("ui.accountTypes.spec.minDeposit"),    v: a.minDeposit, highlight: true },
    { k: t("ui.accountTypes.spec.typicalEurusd"), v: m.typicalSpread },
    { k: t("ui.accountTypes.spec.commission"),    v: commissionLine },
    { k: t("ui.accountTypes.spec.maxLeverage"),   v: m.leverage },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{
        y: -6,
        boxShadow: `0 26px 52px -22px color-mix(in srgb, ${m.accentDeep} 60%, transparent)`,
        borderColor: `color-mix(in srgb, ${m.accentDeep} 55%, var(--line-strong))`,
      }}
      className={`relative group rounded-2xl border flex flex-col h-full ${spanClass}`}
      style={{
        borderColor: `color-mix(in srgb, ${m.accentDeep} 22%, var(--line-strong))`,
        background: `linear-gradient(180deg, color-mix(in srgb, ${m.accentDeep} 4%, var(--surface)) 0%, var(--surface) 70%)`,
      }}
      data-testid={`tier-card-${k}`}
    >
      {/* Top accent stripe · full-width tier-gradient · institutional signature */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] z-10 rounded-t-2xl"
        style={{ background: m.gradient }}
      />

      {/* Admin-controlled "Most Popular" badge · sits ABOVE the card on
          its own line so it acts as a banner indicator rather than a
          corner sticker. Negative top-margin lifts it 14px above the
          accent stripe; left-aligned to the card's content padding so
          it lines up with the tier label below. Only renders on the
          featured tier (controlled from the Admin → Settings tab). */}
      {isFeatured && (
        <div
          className="absolute left-5 -top-3 z-20 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-[6px] font-mono text-[9.5px] uppercase tracking-[0.18em] shadow-md"
          style={{
            background: m.gradient,
            color: ctaTextColor,
            boxShadow: `0 8px 18px -8px color-mix(in srgb, ${m.accentDeep} 60%, transparent), inset 0 1px 0 rgba(255,255,255,0.28)`,
          }}
          data-testid={`tier-featured-${k}`}
        >
          <Sparkles size={10} strokeWidth={2.4} />
          {featuredLabel}
        </div>
      )}

      {/* Soft corner glow · tier-coloured · low opacity */}
      <div
        aria-hidden
        className="absolute -top-20 -right-16 w-56 h-56 rounded-full pointer-events-none opacity-50 group-hover:opacity-90 transition-opacity duration-500"
        style={{ background: `radial-gradient(circle, color-mix(in srgb, ${m.accentDeep} 18%, transparent), transparent 65%)` }}
      />

      <div className="relative p-6 sm:p-7 flex flex-col h-full">
        {/* Header · tier name + INLINE 2-letter code chip (not floating top-right) */}
        <div className="flex items-baseline gap-2.5 mb-1.5 flex-wrap">
          <h3 className="font-display text-[20px] sm:text-[22px] font-medium text-text-1 leading-none tracking-[-0.01em]">
            {a.label}
          </h3>
          <span
            className="font-mono text-[9.5px] uppercase tracking-[0.22em] px-1.5 py-[3px] rounded"
            style={{
              color: m.accentDeep,
              background: `color-mix(in srgb, ${m.accentDeep} 10%, transparent)`,
              border: `1px solid color-mix(in srgb, ${m.accentDeep} 24%, transparent)`,
              lineHeight: 1,
            }}
          >
            {m.code}
          </span>
        </div>
        <p className="text-text-3 text-[12.5px] font-light leading-snug mb-4">{t(`homepage.tiers.${k}.tagline`, m.tagline)}</p>

        {/* Tier highlight pill · short, sharp accent label that gives the
            tier character without taking over the card */}
        <div
          className="inline-flex items-center gap-1.5 self-start h-7 px-2.5 rounded-md text-[12px] font-medium mb-5"
          style={{
            background: `color-mix(in srgb, ${m.accentDeep} 10%, transparent)`,
            border: `1px solid color-mix(in srgb, ${m.accentDeep} 26%, transparent)`,
            color: isLight ? m.accentDeep : m.accent,
          }}
        >
          <Sparkles size={10} strokeWidth={2.2} />
          {t(m.accentLabelKey, m.accentLabel)}
        </div>

        {/* Spec grid · dense, mono, tabular · Min Deposit gets the
            tier-accent colour so the headline number pops without being
            a giant gradient slab */}
        <dl className="grid gap-0">
          {specs.map((s, i) => (
            <div
              key={s.k}
              className={`flex items-baseline justify-between gap-2 py-2.5 ${i < specs.length - 1 ? "border-b" : ""}`}
              style={{ borderColor: "var(--line)" }}
            >
              <dt className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">{s.k}</dt>
              <dd
                className="font-mono tabular-nums text-right max-w-[55%] leading-tight"
                style={{
                  color: s.highlight ? (isLight ? m.accentDeep : m.accent) : "var(--text-1)",
                  fontSize: s.highlight ? "14px" : "12.5px",
                  fontWeight: s.highlight ? 600 : 400,
                }}
              >
                {s.v}
              </dd>
            </div>
          ))}
        </dl>

        {/* Live spread chip · streams from the J4B price hub, tier-colored */}
        <LiveSpreadChip
          tier={k}
          typicalSpread={m.typicalSpread}
          accent={m.accent}
          accentDeep={m.accentDeep}
        />

        {/* CTAs · stacked full-width so labels never wrap in the narrow
            5-across layout · Open uses tier-gradient, View specs neutral */}
        <div className="flex flex-col gap-2.5 mt-auto pt-6">
          <a
            href={m.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`tier-${k}-open`}
            className="btn-gloss inline-flex items-center justify-center gap-1.5 h-11 rounded-lg text-[12.5px] font-semibold whitespace-nowrap"
            style={{
              background: ctaGradient,
              color: ctaTextColor,
              "--gloss-accent": m.accentDeep,
            }}
          >
            {t("common.openAccount")}
            <ArrowRight size={12} strokeWidth={2.4} />
          </a>
          <a
            href={`/account-types/${k}`}
            data-testid={`tier-${k}-details`}
            className="btn-gloss-ghost inline-flex items-center justify-center gap-1.5 h-11 rounded-lg text-[12.5px] font-medium whitespace-nowrap"
            style={{
              color: "var(--text-1)",
              "--gloss-accent": m.accentDeep,
            }}
          >
            {t("common.viewSpecs")}
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function AccountTypesSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Empty selection = neutral state · ALL tiers shown, every chip renders
  // as a gradient-outline ghost. Clicking a chip fills it and narrows the
  // matrix to the selected tiers only.
  const [selected, setSelected] = useState(new Set());
  const [lens, setLens] = useState("all");
  // Per-render locale-aware copy for compare rows + lens labels.
  const COMPARE_ROWS = useMemo(() => getCompareRows(t), [t]);
  const LENSES       = useMemo(() => getLenses(t),       [t]);
  // Live ACCOUNTS = static defaults merged with admin overrides.
  const { accounts: ACCOUNTS } = useSiteSettings();
  // Theme-aware tier CTA palette · the compare-panel "Open" buttons use
  // the EXACT same gradient + ink as the tier cards above so the two
  // surfaces always match.
  const { theme } = useTheme();
  const isLight = theme === "light";
  const tierCtaStyle = (tm) => ({
    background: isLight ? tm.gradientLight : tm.gradient,
    color: isLight ? tm.ctaTextLight : tm.ctaText,
    "--gloss-accent": tm.accentDeep,
  });
  // Per-button CTA routing for the inline "Open" links on each tier
  // card · falls back to direct CRM by default. Social tier keeps its
  // own Trader's Room link (no REGISTER_URL fallback needed) so the
  // override below only applies when TIER_META[k].href === REGISTER_URL.
  const accountTypesCta = useCtaTarget("account_types");
  const resolveTierHref = (raw) => (raw === REGISTER_URL ? accountTypesCta.href : raw);
  const tierLinkProps   = (raw) => (raw === REGISTER_URL ? accountTypesCta.linkProps : {
    href: raw, target: "_blank", rel: "noopener noreferrer",
  });

  const tiers = selected.size === 0 ? KEYS : KEYS.filter((k) => selected.has(k));

  const toggle = (k) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const gridCols = `minmax(130px, 1.2fr) repeat(${tiers.length}, minmax(0, 1fr))`;

  return (
    <section id="accounts" data-testid="account-types-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Institutional headline · centered on every breakpoint */}
        <div className="mb-12 lg:mb-16 text-center">
          <div className="pill-mono inline-flex mb-5">{t("homepage.accountTypes.section.pill", "Account tiers")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem]/[1.08] font-medium tracking-[-0.025em] leading-[1.05] text-text-1 max-w-3xl mx-auto">
            {t("homepage.accountTypes.section.head1", "Five MT5 accounts.")} <span className="text-gradient-brand font-semibold">{t("homepage.accountTypes.section.head2", "One execution engine.")}</span>
          </h2>
        </div>

        {/* The tier-card grid · 3 flagship tiers on top, Social + Cent
            centered underneath on a 6-col track (each card spans 2 cols;
            Social starts at col 2 so the pair sits centered) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5 mb-10">
          {KEYS.map((k, i) => (
            <TierCard key={k} k={k} delay={i * 0.06} spanClass={TIER_SPAN[k]} />
          ))}
        </div>

        {/* Inline compare toggle · low-key */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="account-compare-panel"
            data-testid="toggle-compare-panel"
            className="btn-gloss-ghost inline-flex items-center gap-2 h-11 px-6 rounded-full text-[12.5px] font-medium"
            style={{ color: "var(--text-1)" }}
          >
            <GitCompare size={13} strokeWidth={1.8} style={{ color: "var(--brand)" }} />
            {open ? t("ui.accountTypes.hideCompare") : t("ui.accountTypes.viewCompare")}
            <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Inline comparison panel (no modal) */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              id="account-compare-panel"
              key="compare-panel"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: "auto", marginTop: 32 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid="compare-inline-panel"
            >
              <div
                className="rounded-2xl p-5 sm:p-6 lg:p-8"
                style={{ background: "var(--surface-2)" }}
              >
                {/* Toolbar · centered on mobile, justified on desktop.
                    Mobile: only the LENS filter is shown — tier filtering
                    is handled by the per-row 4-up matrix below (every
                    tier is always visible). Desktop keeps the multi-
                    select tier chips to toggle columns in the wide matrix. */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-center lg:justify-between gap-3 mb-5">
                  <div className="hidden lg:flex items-center gap-2 flex-wrap justify-center lg:justify-start" data-testid="compare-inline-tiers">
                    <div className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[10px] font-mono text-[11px] uppercase tracking-[0.16em] text-text-3" style={{ background: "color-mix(in srgb, var(--text-1) 5%, transparent)" }}>
                      <Filter size={11} strokeWidth={2} /> {t("ui.accountTypes.accountsLabel")}
                    </div>
                    {KEYS.map((k) => {
                      const a = ACCOUNTS[k];
                      const tm = TIER_META[k];
                      const on = selected.has(k);
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => toggle(k)}
                          aria-pressed={on}
                          data-testid={`compare-inline-tier-${k}`}
                          className={`${on ? "btn-gloss" : "btn-tier-outline"} inline-flex items-center justify-center h-8 px-3 rounded-[10px] text-[12px] font-semibold`}
                          style={on
                            ? tierCtaStyle(tm)
                            : {
                                "--tier-grad": isLight ? tm.gradientLight : tm.gradient,
                                "--chip-fill": "var(--surface-2)",
                                "--gloss-accent": tm.accentDeep,
                                color: isLight ? tm.accentDeep : tm.accent,
                              }}
                        >
                          {a.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Lens tabs · centered on mobile, scroll horizontally
                      so labels never clip. 'Lens' prefix hidden on
                      mobile (was eating ~50 px and pushing chips off-
                      screen) — labels alone make the row's purpose
                      obvious. */}
                  <div className="-mx-5 sm:mx-0 overflow-x-auto scrollbar-none flex justify-center lg:justify-start">
                    <div
                      className="inline-flex items-center gap-1 p-1 rounded-[10px] whitespace-nowrap"
                      style={{ background: "color-mix(in srgb, var(--text-1) 5%, transparent)" }}
                      data-testid="compare-inline-lens"
                    >
                      <span className="hidden lg:inline px-2 text-[11px] uppercase tracking-[0.16em] text-text-3 font-mono shrink-0">{t("ui.accountTypes.lensLabel")}</span>
                      {LENSES.map((l) => {
                        const on = lens === l.key;
                        return (
                          <button
                            key={l.key}
                            type="button"
                            onClick={() => setLens(l.key)}
                            data-testid={`compare-inline-lens-${l.key}`}
                            className="h-7 px-2.5 rounded-[8px] text-[12px] font-medium transition-colors shrink-0"
                            style={on ? { background: "var(--brand)", color: "var(--brand-ink)" } : { color: "var(--text-2)" }}
                          >
                            {l.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ============ Mobile/tablet (<xl) · per-row 4-up
                    comparison · ALL 4 tiers visible on every spec
                    row so users can compare side-by-side without
                    scrolling sideways or toggling pills. ============ */}
                <div className="xl:hidden" data-testid="compare-inline-stack-mobile">
                  {/* Tier header strip · sticky column labels with
                      accent colour stripe so users always know which
                      mini-cell belongs to which tier as they scroll. */}
                  <div
                    className="grid grid-cols-5 gap-2 mb-2 sticky top-[60px] z-10 -mx-1 px-1 py-2 rounded-lg"
                    style={{ background: "var(--surface-2)" }}
                    data-testid="compare-inline-mobile-header"
                  >
                    {KEYS.map((k) => {
                      const a = ACCOUNTS[k];
                      const tm = TIER_META[k];
                      const accentDeep = tm?.accentDeep || "var(--brand)";
                      return (
                        <div
                          key={k}
                          className="text-center"
                          data-testid={`compare-inline-mobile-header-${k}`}
                        >
                          <div
                            className="h-1 w-8 rounded-full mx-auto mb-1.5"
                            style={{ background: accentDeep }}
                          />
                          <div
                            className="font-display text-[13px] font-semibold leading-tight"
                            style={{ color: accentDeep }}
                          >
                            {a.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Spec rows · spec label on top, 4 mini-cells below
                      (one per tier) so users compare ALL accounts on
                      every row at once. Dims rows that don't match
                      the active lens. */}
                  <div className="rounded-xl overflow-hidden" style={{ background: "var(--surface)" }} data-testid="compare-inline-mobile-rows">
                    {COMPARE_ROWS.map((row, idx) => {
                      const dim = lens !== "all" && row.lens !== lens;
                      return (
                        <div
                          key={row.label}
                          className="px-3 py-3 transition-opacity"
                          style={{
                            borderBottom: idx === COMPARE_ROWS.length - 1 ? "none" : "1px solid color-mix(in srgb, var(--text-1) 5%, transparent)",
                            opacity: dim ? 0.35 : 1,
                          }}
                          data-testid={`compare-inline-mobile-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <div className="text-text-3 text-[11px] uppercase tracking-[0.14em] font-mono leading-tight mb-2 text-center">
                            {row.label}
                          </div>
                          <div className="grid grid-cols-5 gap-1.5">
                            {KEYS.map((k) => {
                              const a = ACCOUNTS[k];
                              const tm = TIER_META[k];
                              const accentDeep = tm?.accentDeep || "var(--brand)";
                              return (
                                <div
                                  key={k}
                                  className="rounded-md py-1.5 px-1 text-center min-w-0 flex items-center justify-center"
                                  style={{
                                    background: `color-mix(in srgb, ${accentDeep} 4%, var(--surface-2))`,
                                    minHeight: 30,
                                  }}
                                  data-testid={`compare-inline-mobile-cell-${k}-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                  <CompactCompareCell value={row.get(a)} accent={accentDeep} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 4 Open-account CTAs · one per tier, in tier accent
                      colour, 2×2 grid so each gets full breathing room. */}
                  <div className="grid grid-cols-2 gap-2 mt-5" data-testid="compare-inline-mobile-ctas">
                    {KEYS.map((k) => {
                      const a = ACCOUNTS[k];
                      const tm = TIER_META[k];
                      return (
                        <a
                          key={k}
                          {...tierLinkProps(TIER_META[k].href)}
                          className="btn-gloss inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-[10px] text-[12.5px] font-semibold"
                          style={tierCtaStyle(tm)}
                          data-testid={`compare-inline-mobile-open-${k}`}
                        >
                          <span className="truncate">{t("ui.accountTypes.openTier", { label: a.label })}</span> <ArrowRight size={12} className="shrink-0" />
                        </a>
                      );
                    })}
                  </div>
                </div>

                {/* ============ Desktop · side-by-side matrix (xl+ only) ============ */}
                <div className="hidden xl:block">
                {/* Tier header strip · TIER-COLOURED · matches /account-comparison */}
                <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: gridCols }}>
                  <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 flex items-center pl-2">
                    {lens === "all" ? t("ui.accountTypes.spec") : t("ui.accountTypes.highlighting", { lens: LENSES.find(l => l.key === lens)?.label || lens })}
                  </div>
                  {tiers.map((k) => {
                    const a = ACCOUNTS[k];
                    const tm = TIER_META[k];
                    const accentDeep = tm?.accentDeep || "var(--brand)";
                    return (
                      <div
                        key={k}
                        className="rounded-[10px] p-3 text-center border"
                        style={{
                          borderColor: `color-mix(in srgb, ${accentDeep} 30%, var(--line-strong))`,
                          background: `color-mix(in srgb, ${accentDeep} 6%, var(--surface-2))`,
                          borderTop: `3px solid ${accentDeep}`,
                        }}
                      >
                        <div className="font-display text-[14px] font-medium" style={{ color: accentDeep }}>{a.label}</div>
                        <div className="font-mono text-[12px] uppercase tracking-wider mt-0.5 text-text-3 tabular-nums">{a.minDeposit} {t("ui.accountTypes.minSuffix")}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Rows */}
                <div className="rounded-2xl border overflow-hidden mt-2" style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }} data-testid="compare-inline-table">
                  {COMPARE_ROWS.map((row, idx) => {
                    const dim = lens !== "all" && row.lens !== lens;
                    return (
                      <div
                        key={row.label}
                        className="grid gap-2 items-center px-3 py-3 border-b last:border-0 transition-opacity"
                        style={{
                          gridTemplateColumns: gridCols,
                          borderColor: "var(--line)",
                          background: idx % 2 === 1 ? "color-mix(in srgb, var(--surface-3) 30%, transparent)" : "transparent",
                          opacity: dim ? 0.4 : 1,
                        }}
                      >
                        <div className="text-text-3 text-[12px] uppercase tracking-wider font-mono pl-2 truncate">{row.label}</div>
                        {tiers.map((k) => (
                          <div key={k} className="text-center"><CompareCell value={row.get(ACCOUNTS[k])} /></div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Open buttons row · TIER-COLOURED · matches /account-comparison */}
                <div className="grid gap-2 mt-4" style={{ gridTemplateColumns: gridCols }}>
                  <div />
                  {tiers.map((k) => {
                    const tm = TIER_META[k];
                    return (
                      <a
                        key={k}
                        {...tierLinkProps(TIER_META[k].href)}
                        className="btn-gloss inline-flex items-center justify-center gap-1.5 h-11 rounded-[10px] text-[12.5px] font-semibold"
                        style={tierCtaStyle(tm)}
                        data-testid={`compare-inline-open-${k}`}
                      >
                        {t("common.open")} <ArrowRight size={12} strokeWidth={2.5} />
                      </a>
                    );
                  })}
                </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
